import type { User } from "firebase/auth"
import type { LiveDocument } from "popopo-msg"
import { useLiveGenres } from "popopo-lib-front/firestore-access"
import { api, firestore } from "../../core"
import { useFieldArray } from "react-hook-form"
import { Button } from "react-bootstrap"
import { useToast } from "popopo-lib-front/hook"
import { ManagedFloatingInput, ManagedFloatingSelect } from "popopo-lib-front/component"
import { z } from "zod/v4"
import { useZodForm } from "popopo-lib-front/zod-form"

const schema = z.object({
  genreId: z.string().min(1, "必須要素です。"),
  tags: z.array(z.object({
    value: z.string().min(1, "必須要素です。"),
  })),
})

export const SpaceLiveUpdateForm = (props: { firebase: User; live: LiveDocument }) => {
  const genres = useLiveGenres(firestore)
  const toast = useToast()

  const form = useZodForm({
    schema,
    mode: "onBlur",
    defaultValues: {
      genreId: props.live.genre_id,
      tags: props.live.tags.map(value => ({ value }))
    },
  })
  const tags = useFieldArray({
    control: form.control,
    name: "tags",
  })

  if (genres === "loading") {
    return "Loading..."
  }


  const onLiveUpdate = async (value: z.infer<typeof schema>) => {
    try {
      const idToken = await props.firebase.getIdToken()
      const res = await api.PUT("/api/v2/spaces/{space_key}/lives/{live_id}", {
        params: { path: { space_key: props.live.space_key, live_id: props.live.id } },
        headers: {
          authorization: `Bearer ${idToken}`,
        },
        body: { genreId: value.genreId, tags: value.tags.map(x => x.value) },
      })
      if (res.data == null) {
        toast.add(
          { type: "danger", title: "Live更新に失敗しました", message: JSON.stringify(res) }
        )
      }
    } catch (e) {
      toast.add(
        { type: "danger", title: "Live更新に失敗しました", message: JSON.stringify(e) }
      )
      console.error(e)
    }
  }

  const { isSubmitting, isValid } = form.formState

  return (
    <form className="d-flex w-100 flex-column gap-3" onSubmit={form.handleSubmit((value) => onLiveUpdate(value))}>
      <ManagedFloatingSelect control={form.control} name="genreId" label="ジャンル" options={
        genres.map((x) => ({
          value: x.id,
          label: x.label,
        }))
      } />

      <Button type="button" onClick={() => tags.append({ value: new Date().toLocaleString() })}>
        タグ入力欄を追加
      </Button>

      {tags.fields.map((field, index) => (
        <ManagedFloatingInput key={field.id} label="タグ" control={form.control} name={`tags.${index}.value`} inputGroupAfter={
          <Button type="button" variant="danger" onClick={() => tags.remove(index)}>
            削除
          </Button>
        } />
      ))}

      <button type="submit" className="btn btn-primary form-control" disabled={isSubmitting || !isValid}>
        Update Live
      </button>
    </form>
  )
}
